import { idbAll, idbDelete, idbGet, idbPut, idbPutMany } from './idb.js';
import { byOrder, makeOp, migrateLegacyAuditQueue, retried } from './queueCore.js';
import { USER_STORAGE_KEY } from '../config/index.js';

/**
 * The offline action queue shared by every field screen (audit scans / ticks / sign-offs, blind counts, first-walk
 * binds and exceptions, custody moves / returns, recondition, damage reports), kept in IndexedDB.
 * Screens enqueue; the sync engine (syncEngine.js) replays the queue in order through /api/field-sync.
 */

const LEGACY_QUEUE_KEY = 'audit_walk_queue_v1';
const LEGACY_CACHE_KEY = 'audit_walk_cache_v1';
const listeners = new Set();
let channel = null;
try {
  channel = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('assetos-field-queue') : null;
} catch {
  channel = null;
}

/** The signed-in user's id: each op belongs to whoever took it, and only that user's session ever syncs it. */
export function currentUserId() {
  try {
    return JSON.parse(localStorage.getItem(USER_STORAGE_KEY) || 'null')?.id ?? null;
  } catch {
    return null;
  }
}
const mine = (o) => !o.userId || !currentUserId() || o.userId === currentUserId();

let snapshot = [];
let ready = null;
let lastSeq = 0;

async function reload() {
  const all = (await idbAll('ops')).sort(byOrder);
  lastSeq = all.reduce((m, o) => Math.max(m, o.seq || 0), lastSeq);
  // Another user's actions on a shared device stay put (they sync when that user signs in again) but are not shown.
  snapshot = all.filter(mine);
  listeners.forEach((fn) => fn(snapshot));
  return snapshot;
}

function changed() {
  channel?.postMessage('changed');
  return reload();
}
if (channel) channel.onmessage = () => reload();

/** First load: moves any queue left in localStorage by the earlier audit page into IndexedDB (once). */
async function migrate() {
  try {
    const raw = localStorage.getItem(LEGACY_QUEUE_KEY);
    if (raw) {
      const ops = migrateLegacyAuditQueue(JSON.parse(raw));
      if (ops.length) await idbPutMany('ops', ops);
      localStorage.removeItem(LEGACY_QUEUE_KEY);
    }
    localStorage.removeItem(LEGACY_CACHE_KEY);
  } catch {
    /* a corrupt legacy queue is dropped rather than blocking the app */
  }
}

export function ensureReady() {
  if (!ready) ready = migrate().then(reload);
  return ready;
}

/** Current queue (oldest first). */
export async function listOps() {
  await ensureReady();
  return snapshot;
}

export const currentOps = () => snapshot;

/** Calls fn(ops) on every change (this tab or another); returns an unsubscribe function. */
export function subscribe(fn) {
  listeners.add(fn);
  ensureReady().then(() => fn(snapshot));
  return () => listeners.delete(fn);
}

/** Adds an action to the queue; returns the stored op. */
export async function enqueue(input) {
  await ensureReady();
  const userId = currentUserId();
  const op = { ...makeOp(input, Date.now(), lastSeq), ...(userId ? { userId } : {}) };
  lastSeq = op.seq;
  await idbPut('ops', op);
  await changed();
  return op;
}

export async function putOp(op) {
  await idbPut('ops', op);
  await changed();
}

export async function removeOp(clientId) {
  await idbDelete('ops', clientId);
  await changed();
}

/** Conflicts list: Retry puts the action back in the queue (keeping its original place in the order). */
export async function retryOp(clientId) {
  const op = await idbGet('ops', clientId);
  if (op) await putOp(retried(op));
}

/** Conflicts list: Discard drops the action from this device for good. */
export const discardOp = removeOp;

/** Store adapter for the sync engine (writes without broadcasting each step; the engine broadcasts once at the end). */
export const engineStore = {
  list: async () => (await idbAll('ops')).filter(mine).sort(byOrder),
  put: (op) => idbPut('ops', op),
  remove: (id) => idbDelete('ops', id),
  done: () => changed(),
};

export async function getMeta(key) {
  return (await idbGet('meta', key))?.value;
}

export async function setMeta(key, value) {
  await idbPut('meta', { key, value });
}
